import * as React from 'react';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * PageHeader — the standard page title block.
 *
 * Generalizes the pattern platform-admin proved out with AdminPageHeader
 * (the one section of the app that standardized its headers, and the most
 * internally consistent as a result). Surface-neutral: no border/background
 * band, so it works inside the admin shell, /me pages, and cards alike.
 *
 *   <PageHeader
 *     title="Candidates"
 *     description="Search and manage your talent pool."
 *     actions={<Button>Import</Button>}
 *   />
 *
 * Title style is THE standard: text-page-title text-foreground.
 */

export interface PageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  /** Renders in a <div> so callers can pass rich content (badge rows). */
  description?: React.ReactNode;
  icon?: LucideIcon;
  /** Small back link rendered above the title. */
  backHref?: string;
  /** Back as an action (e.g. router.back()) when there's no stable href. */
  onBack?: () => void;
  backLabel?: string;
  /** Right-side controls — primary action first, secondary after. */
  actions?: React.ReactNode;
  /** Optional row below the title band (filters, tabs, search). */
  children?: React.ReactNode;
}

const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  (
    { title, description, icon: Icon, backHref, onBack, backLabel = 'Back', actions, children, className, ...props },
    ref,
  ) => {
    const backClass =
      'mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm';
    return (
    <div ref={ref} className={cn('space-y-4', className)} {...props}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          {backHref ? (
            <Link href={backHref} className={backClass}>
              <ArrowLeft className="h-3 w-3" aria-hidden="true" />
              {backLabel}
            </Link>
          ) : onBack ? (
            <button type="button" onClick={onBack} className={backClass}>
              <ArrowLeft className="h-3 w-3" aria-hidden="true" />
              {backLabel}
            </button>
          ) : null}
          <h1 className="flex items-center gap-2 text-page-title text-foreground">
            {Icon && <Icon className="h-6 w-6 text-primary" aria-hidden="true" />}
            <span className="truncate">{title}</span>
          </h1>
          {description ? (
            <div className="text-sm text-muted-foreground">{description}</div>
          ) : null}
        </div>
        {actions ? (
          // min-w-0 (not shrink-0): action sets can be wide (filters + search);
          // the container must be allowed to shrink and wrap or the page grows
          // a horizontal scrollbar at mid viewports.
          <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {children}
    </div>
    );
  },
);
PageHeader.displayName = 'PageHeader';

export { PageHeader };
